import { createReducer, on, Action, State, props } from '@ngrx/store';
import {
  DeletedItem,
  EntityMediaItem,
  EntityMediaResponse,
  EnumMediaEntities,
  FolderCreationResponse,
  MediaListFullResponse,
  MediaRecentListFullResponse,
  MediaStatsFullResponse,
  MediaTypeItem,
  MovedItem,
  UploadedItem,
} from '../../../api/models';
import { ILoaderActionPayload, LoadingState } from '../models/loader.model';
import { fileManagerAction } from './file-manager.actions';

export interface FileManagerState {
  recents: {
    state: ILoaderActionPayload;
    data?: MediaRecentListFullResponse;
  };
  stats: {
    state: ILoaderActionPayload;
    data?: MediaStatsFullResponse;
  };
  folder: {
    state: ILoaderActionPayload;
    data?: MediaListFullResponse;
  };
  mkFolder: {
    state: ILoaderActionPayload;
    data?: FolderCreationResponse;
  };
  deleteOne: {
    state: ILoaderActionPayload;
    data?: DeletedItem;
  };
  moveOne: {
    state: ILoaderActionPayload;
    data?: MovedItem;
  };
  uploadOne: {
    state: ILoaderActionPayload;
    data?: UploadedItem;
  };
  uploadEntityOne: {
    state: ILoaderActionPayload;
    data?: EntityMediaItem;
    entity_id?: number;
  };
  downloadOne: {
    state: ILoaderActionPayload;
    data?: { fileData: Blob };
  };
  loadEntityMedia: {
    [key in EnumMediaEntities]: {
      limit?: number;
      skip?: number;
      state: ILoaderActionPayload;
      data?: EntityMediaResponse;
    };
  };
  deleteEntityMedia: {
    [key in EnumMediaEntities]: {
      limit?: number;
      skip?: number;
      state: ILoaderActionPayload;
      data?: EntityMediaItem;
    };
  };
  mediaTypes: {
    [key in EnumMediaEntities]: {
      state: ILoaderActionPayload;
      data?: MediaTypeItem[];
    };
  };
  singleMediaType: {
    [key in EnumMediaEntities]: {
      state: ILoaderActionPayload;
      data?: MediaTypeItem;
    };
  };
}

const initialState: FileManagerState = {
  recents: { state: { value: LoadingState.NONE } },
  stats: { state: { value: LoadingState.NONE } },
  folder: { state: { value: LoadingState.NONE } },
  mkFolder: { state: { value: LoadingState.NONE } },
  deleteOne: { state: { value: LoadingState.NONE } },
  moveOne: { state: { value: LoadingState.NONE } },
  uploadOne: { state: { value: LoadingState.NONE } },
  uploadEntityOne: { state: { value: LoadingState.NONE } },
  downloadOne: { state: { value: LoadingState.NONE } },
  loadEntityMedia: {
    [EnumMediaEntities.printer]: { state: { value: LoadingState.NONE } },
    [EnumMediaEntities.user]: { state: { value: LoadingState.NONE } },
    [EnumMediaEntities.company_logos]: { state: { value: LoadingState.NONE } },
    [EnumMediaEntities.profiles]: { state: { value: LoadingState.NONE } },
  },
  deleteEntityMedia: {
    [EnumMediaEntities.printer]: { state: { value: LoadingState.NONE } },
    [EnumMediaEntities.user]: { state: { value: LoadingState.NONE } },
    [EnumMediaEntities.company_logos]: { state: { value: LoadingState.NONE } },
    [EnumMediaEntities.profiles]: { state: { value: LoadingState.NONE } },
  },
  mediaTypes: {
    [EnumMediaEntities.printer]: { state: { value: LoadingState.NONE } },
    [EnumMediaEntities.user]: { state: { value: LoadingState.NONE } },
    [EnumMediaEntities.company_logos]: { state: { value: LoadingState.NONE } },
    [EnumMediaEntities.profiles]: { state: { value: LoadingState.NONE } },
  },
  singleMediaType: {
    [EnumMediaEntities.printer]: { state: { value: LoadingState.NONE } },
    [EnumMediaEntities.user]: { state: { value: LoadingState.NONE } },
    [EnumMediaEntities.company_logos]: { state: { value: LoadingState.NONE } },
    [EnumMediaEntities.profiles]: { state: { value: LoadingState.NONE } },
  },
};

const reducer = createReducer(
  initialState,
  on(fileManagerAction.recentsLoad, (state) => ({ ...state, recents: { state: { value: LoadingState.LOADING } } })),
  on(fileManagerAction.recentsDone, (state, props) => ({
    ...state,
    recents: { state: { ...props.state }, data: props.data },
  })),
  on(fileManagerAction.recentsSetState, (state, props) => ({ ...state, recent: { state: { ...props.state } } })),
  on(fileManagerAction.statsLoad, (state) => ({ ...state, stats: { state: { value: LoadingState.LOADING } } })),
  on(fileManagerAction.statsDone, (state, props) => ({
    ...state,
    stats: { state: { ...props.state }, data: props.data },
  })),
  on(fileManagerAction.statsSetState, (state, props) => ({ ...state, stats: { state: { ...props.state } } })),
  on(fileManagerAction.folderLoad, (state) => ({ ...state, folder: { state: { value: LoadingState.LOADING } } })),
  on(fileManagerAction.folderDone, (state, props) => ({
    ...state,
    folder: { state: { ...props.state }, data: props.data },
  })),
  on(fileManagerAction.folderSetState, (state, props) => ({ ...state, folder: { state: { ...props.state } } })),
  on(fileManagerAction.mkFolder, (state) => ({ ...state, mkFolder: { state: { value: LoadingState.LOADING } } })),
  on(fileManagerAction.mkFolderDone, (state, props) => ({
    ...state,
    mkFolder: { state: { ...props.state }, data: props.data },
  })),
  on(fileManagerAction.mkFolderSetState, (state, props) => ({ ...state, mkFolder: { state: { ...props.state } } })),
  on(fileManagerAction.deleteOne, (state) => ({ ...state, deleteOne: { state: { value: LoadingState.LOADING } } })),
  on(fileManagerAction.deleteOneDone, (state, props) => ({
    ...state,
    deleteOne: { state: { ...props.state }, data: props.data },
  })),
  on(fileManagerAction.deleteOneSetState, (state, props) => ({ ...state, deleteOne: { state: { ...props.state } } })),
  on(fileManagerAction.moveOne, (state) => ({ ...state, moveOne: { state: { value: LoadingState.LOADING } } })),
  on(fileManagerAction.moveOneDone, (state, props) => ({
    ...state,
    moveOne: { state: { ...props.state }, data: props.data },
  })),
  on(fileManagerAction.moveOneSetState, (state, props) => ({ ...state, moveOne: { state: { ...props.state } } })),
  on(fileManagerAction.uploadOne, (state) => ({ ...state, uploadOne: { state: { value: LoadingState.LOADING } } })),
  on(fileManagerAction.uploadOneDone, (state, props) => ({
    ...state,
    uploadOne: { state: { ...props.state }, data: props.data },
  })),
  on(fileManagerAction.uploadOneSetState, (state, props) => ({ ...state, uploadOne: { state: { ...props.state } } })),
  on(fileManagerAction.uploadEntityOne, (state) => ({
    ...state,
    uploadEntityOne: { state: { value: LoadingState.LOADING } },
  })),
  on(fileManagerAction.uploadEntityOneDone, (state, props) => ({
    ...state,
    uploadEntityOne: { state: { ...props.state }, data: props.data },
  })),
  on(fileManagerAction.uploadEntityOneSetState, (state, props) => ({
    ...state,
    uploadEntityOne: { state: { ...props.state } },
  })),
  on(fileManagerAction.downloadOne, (state) => ({ ...state, downloadOne: { state: { value: LoadingState.LOADING } } })),
  on(fileManagerAction.downloadOneDone, (state, props) => ({
    ...state,
    downloadOne: { state: { ...props.state }, data: props.data },
  })),
  on(fileManagerAction.downloadOneSetState, (state, props) => ({
    ...state,
    downloadOne: { state: { ...props.state } },
  })),
  on(fileManagerAction.resetState, () => ({ ...initialState })),

  //load media

  on(fileManagerAction.loadOneMedia, (state, props) => ({
    ...state,
    loadEntityMedia: {
      ...state.loadEntityMedia,
      [props.entity as EnumMediaEntities]: {
        limit: props.limit,
        skip: props.skip,
        state: { value: LoadingState.LOADING },
      },
    },
  })),
  on(fileManagerAction.loadOneMediaDone, (state, props) => ({
    ...state,
    loadEntityMedia: {
      ...state.loadEntityMedia,
      [props.entity as EnumMediaEntities]: { state: props.state, data: props.data },
    },
  })),
  on(fileManagerAction.loadOneMediaSetState, (state, props) => ({
    ...state,
    loadEntityMedia: {
      ...state.loadEntityMedia,
      [props.entity as EnumMediaEntities]: { state: { value: props.state.value, message: undefined } },
    },
  })),
  // deleteOneMedia
  on(fileManagerAction.deleteOneMedia, (state, props) => ({
    ...state,
    deleteEntityMedia: {
      ...state.deleteEntityMedia,
      [props.entity as EnumMediaEntities]: { state: { value: LoadingState.LOADING } },
    },
  })),
  on(fileManagerAction.deleteOneMediaDone, (state, props) => ({
    ...state,
    deleteEntityMedia: {
      ...state.deleteEntityMedia,
      [props.entity as EnumMediaEntities]: { state: props.state, data: props.data },
    },
  })),
  on(fileManagerAction.deleteOneMediaSetState, (state, props) => ({
    ...state,
    deleteEntityMedia: {
      ...state.deleteEntityMedia,
      [props.entity as EnumMediaEntities]: { state: { value: props.state.value, message: undefined } },
    },
  })),
  // mediaTypes
  on(fileManagerAction.loadMediaTypes, (state, props) => ({
    ...state,
    mediaTypes: {
      ...state.mediaTypes,
      [props.entity as EnumMediaEntities]: { state: { value: LoadingState.LOADING } },
    },
  })),
  on(fileManagerAction.doneMediaTypes, (state, props) => ({
    ...state,
    mediaTypes: {
      ...state.mediaTypes,
      [props.entity as EnumMediaEntities]: { state: props.state, data: props.data },
    },
  })),
  // singleMediaType
  on(fileManagerAction.loadOneMediaType, (state, props) => ({
    ...state,
    singleMediaType: {
      ...state.singleMediaType,
      [props.entity as EnumMediaEntities]: { state: { value: LoadingState.LOADING } },
    },
  })),
  on(fileManagerAction.loadOneMediaTypeDone, (state, props) => ({
    ...state,
    singleMediaType: {
      ...state.singleMediaType,
      [props.entity as EnumMediaEntities]: { state: props.state, data: props.data },
    },
  })),
);

export function fileManagerReducer(state: FileManagerState | undefined, action: Action) {
  return reducer(state, action);
}
